import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  LinearProgress,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  pressFormService,
  machineService,
  productService,
  notificationService,
  dashboardService,
} from '../services/api';
import { getNotificationDestination } from '../utils/notificationNavigation';

const NOTIFICATION_TYPES = [
  { value: '', label: 'Усі типи' },
  { value: 'order_created', label: 'Нове замовлення' },
  { value: 'purchase_status_changed', label: 'Зміна статусу закупівлі' },
  { value: 'purchase_completed', label: 'Закупівля виконана' },
  { value: 'production_status_changed', label: 'Зміна статусу виробництва' },
  { value: 'machine_break_planned', label: 'Запланована перерва на ТПА' },
  { value: 'quality_assigned', label: 'Призначено контроль якості' },
  { value: 'quality_failed', label: 'Контроль якості не пройдено' },
  { value: 'quality_passed', label: 'Контроль якості пройдено' },
  { value: 'stock_transaction', label: 'Операція складу' },
  { value: 'stock_finished_receipt', label: 'Прихід готової продукції' },
  { value: 'stock_finished_shipment', label: 'Відвантаження готової продукції' },
  { value: 'crushing_created', label: 'Нове дроблення' },
  { value: 'crushing_confirmed', label: 'Підтвердження дроблення' },
  { value: 'production_report_created', label: 'Звіт виробництва' },
  { value: 'purchase_request_created', label: 'Нова заявка на закупівлю' },
  { value: 'purchase_approval_decided', label: 'Погодження закупівлі' },
  { value: 'purchase_awaiting_approval', label: 'Закупівля на погодження' },
];

function fmtDt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('uk-UA');
  } catch {
    return iso;
  }
}

function fmtDeviationPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n}%`;
}

function deviationChipColor(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return 'default';
  const a = Math.abs(Number(pct));
  if (a > 15) return 'error';
  if (a > 5) return 'warning';
  return 'success';
}

function stockKindLabel(kind) {
  if (kind === 'raw_material') return 'Сировина';
  if (kind === 'dye') return 'Барвник';
  if (kind === 'packaging') return 'Упаковка';
  return kind || '—';
}

const WEEKDAYS_SHORT_UK = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

function weekdayUk(isoDate, weekdayNum) {
  if (weekdayNum != null && weekdayNum >= 0 && weekdayNum <= 6) {
    return WEEKDAYS_SHORT_UK[weekdayNum];
  }
  if (!isoDate) return '';
  const d = new Date(`${isoDate}T12:00:00`);
  return WEEKDAYS_SHORT_UK[d.getDay()];
}

/** Вертикальні міні-стовпчики (без зовнішніх бібліотек графіків) */
function MiniBars({ values, labels, colors, max, heightPx = 44 }) {
  const nums = (values || []).map((x) => Number(x) || 0);
  const m = max != null && max > 0 ? max : Math.max(1, ...nums, 0.001);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: heightPx + 18, mt: 1 }}>
      {nums.map((v, i) => {
        const h = Math.min(heightPx, Math.round((v / m) * heightPx));
        return (
          <Box key={i} sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <Box
              sx={{
                height: heightPx,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 32,
                  height: v > 0 ? Math.max(6, h) : 0,
                  borderRadius: '6px 6px 2px 2px',
                  bgcolor: colors?.[i] || 'primary.main',
                  opacity: v > 0 ? 1 : 0.2,
                  transition: 'height 0.25s ease',
                }}
              />
            </Box>
            {labels && labels[i] != null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem', lineHeight: 1.1, display: 'block', mt: 0.25 }}
                noWrap
              >
                {labels[i]}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function ExecutiveSummaryCards({
  scrollToDetail,
  prodSnap,
  prodLoading,
  prodError,
  outSnap,
  outLoading,
  outError,
  matSnap,
  matLoading,
  matError,
  fgSnap,
  fgLoading,
  fgError,
}) {
  const orderMap = Object.fromEntries((prodSnap?.orders_by_status || []).map((r) => [r.key, r]));
  const prodBarKeys = ['planned', 'prep', 'running', 'completed'];
  const prodBarVals = prodBarKeys.map((k) => orderMap[k]?.count ?? 0);
  const prodBarLabels = ['План', 'Підг.', 'В роб.', 'Зав.'];
  const prodBarColors = ['#78909c', '#ffb74d', '#42a5f5', '#66bb6a'];

  const rs = prodSnap?.risky_summary;
  const riskyTotal = rs
    ? (Number(rs.late_start) || 0) +
      (Number(rs.deadline_soon) || 0) +
      (Number(rs.overdue_plan) || 0) +
      (Number(rs.customer_deadline_soon) || 0)
    : 0;

  const outFacts = outSnap?.periods
    ? [outSnap.periods.today?.fact, outSnap.periods.week?.fact, outSnap.periods.month?.fact].map((x) =>
        Number(x) || 0
      )
    : [0, 0, 0];
  const outLabels = ['День', '7д', 'Міс.'];

  const matCrit = matSnap?.critical_stock?.count ?? 0;
  const matOver = matSnap?.purchases?.overdue_count ?? 0;
  const matHang = matSnap?.unconfirmed_receipts?.total_logistics_rows ?? 0;
  const matBars = [matCrit, matOver, matHang];
  const matLabels = ['Крит.', 'Простр.', 'Висячі'];

  const fgDailyQty = (fgSnap?.shipments?.daily_last_7_days || []).map((d) => Number(d.quantity) || 0);
  const fgDayLabels = (fgSnap?.shipments?.daily_last_7_days || []).map((d) => {
    if (!d.date) return '';
    const parts = d.date.split('-');
    return parts.length === 3 ? `${parts[2]}.${parts[1]}` : d.date.slice(5);
  });

  const anyLoading = prodLoading || outLoading || matLoading || fgLoading;

  return (
    <Paper
      id="dashboard-executive-summary"
      elevation={0}
      variant="outlined"
      sx={{ p: 2, mb: 3, mt: 2, bgcolor: 'background.default' }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Швидкий огляд
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Натисніть картку — перехід до детального звіту нижче. Повний вигляд таблиць збережено під оглядом.
          </Typography>
        </Box>
        {anyLoading && <CircularProgress size={22} />}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%', overflow: 'visible' }}>
            <CardActionArea
              onClick={() => scrollToDetail('dashboard-detail-production')}
              sx={{
                p: 1.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Typography variant="subtitle2" color="primary">
                  Виробництво
                </Typography>
                <ChevronRightIcon fontSize="small" color="action" />
              </Box>
              {prodError && (
                <Typography variant="caption" color="error">
                  Помилка завантаження
                </Typography>
              )}
              {prodLoading && !prodSnap && !prodError && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {prodSnap && (
                <>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    <Chip size="small" label={`Запущено: ${orderMap.running?.count ?? 0}`} color="primary" variant="outlined" />
                    <Chip
                      size="small"
                      label={`Ризики: ${riskyTotal}`}
                      color={riskyTotal ? 'warning' : 'default'}
                      variant={riskyTotal ? 'filled' : 'outlined'}
                    />
                  </Box>
                  <MiniBars values={prodBarVals} labels={prodBarLabels} colors={prodBarColors} />
                  <Typography variant="caption" color="text.secondary">
                    Замовлень за етапами (кількість)
                  </Typography>
                </>
              )}
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardActionArea
              onClick={() => scrollToDetail('dashboard-detail-output')}
              sx={{
                p: 1.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="subtitle2" color="primary">
                  Випуск (факт)
                </Typography>
                <ChevronRightIcon fontSize="small" color="action" />
              </Box>
              {outError && (
                <Typography variant="caption" color="error">
                  Помилка завантаження
                </Typography>
              )}
              {outLoading && !outSnap && !outError && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {outSnap && (
                <>
                  <MiniBars values={outFacts} labels={outLabels} colors={['#5c6bc0', '#7e57c2', '#3949ab']} heightPx={40} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Сьогодні: факт {outSnap.periods?.today?.fact ?? '—'} / план {outSnap.periods?.today?.plan ?? '—'} шт
                  </Typography>
                  {outSnap.periods?.today?.deviation_pct != null && (
                    <Chip
                      size="small"
                      sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                      color={deviationChipColor(outSnap.periods.today.deviation_pct)}
                      label={`Відхил. сьогодні: ${fmtDeviationPct(outSnap.periods.today.deviation_pct)}`}
                    />
                  )}
                </>
              )}
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardActionArea
              onClick={() => scrollToDetail('dashboard-detail-materials')}
              sx={{
                p: 1.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="subtitle2" color="primary">
                  Матеріали / логістика
                </Typography>
                <ChevronRightIcon fontSize="small" color="action" />
              </Box>
              {matError && (
                <Typography variant="caption" color="error">
                  Помилка завантаження
                </Typography>
              )}
              {matLoading && !matSnap && !matError && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {matSnap && (
                <>
                  <MiniBars values={matBars} labels={matLabels} colors={['#ef5350', '#ff9800', '#9e9e9e']} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    У дорозі: {matSnap.purchases?.in_transit_value ?? 0} грн · очікує прийом закупівель:{' '}
                    {matSnap.purchases?.completed_awaiting_receipt_count ?? 0}
                  </Typography>
                </>
              )}
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardActionArea
              onClick={() => scrollToDetail('dashboard-detail-finished-goods')}
              sx={{
                p: 1.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="subtitle2" color="primary">
                  Готова продукція
                </Typography>
                <ChevronRightIcon fontSize="small" color="action" />
              </Box>
              {fgError && (
                <Typography variant="caption" color="error">
                  Помилка завантаження
                </Typography>
              )}
              {fgLoading && !fgSnap && !fgError && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {fgSnap && (
                <>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Відвантаження по днях (шт, 7 дн.)
                    </Typography>
                    <MiniBars
                      values={fgDailyQty.length ? fgDailyQty : [0, 0, 0, 0, 0, 0, 0]}
                      labels={fgDayLabels.length ? fgDayLabels : ['—', '—', '—', '—', '—', '—', '—']}
                      colors={['#26a69a', '#4db6ac', '#80cbc4', '#26a69a', '#4db6ac', '#80cbc4', '#00897b']}
                      heightPx={36}
                    />
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Залишок: ${fgSnap.balances_aggregate?.total_units_on_hand ?? 0} шт`}
                    />
                    <Chip
                      size="small"
                      color={fgSnap.unconfirmed_receipts?.count ? 'warning' : 'default'}
                      label={`Без підтв. приходу: ${fgSnap.unconfirmed_receipts?.count ?? 0}`}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Відвантаження сьогодні: {fgSnap.shipments?.periods?.today?.quantity ?? 0} шт
                  </Typography>
                </>
              )}
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Детальні таблиці та фільтри — нижче ↓
        </Typography>
        <LinearProgress
          variant="determinate"
          value={
            [prodSnap, outSnap, matSnap, fgSnap].filter(Boolean).length * 25
          }
          sx={{ mt: 0.75, height: 4, borderRadius: 1, maxWidth: 360 }}
        />
      </Box>
    </Paper>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pressForms: 0,
    machines: 0,
    products: 0,
    loading: true,
  });
  const [prodSnap, setProdSnap] = useState(null);
  const [prodLoading, setProdLoading] = useState(true);
  const [prodError, setProdError] = useState('');
  const [outSnap, setOutSnap] = useState(null);
  const [outLoading, setOutLoading] = useState(true);
  const [outError, setOutError] = useState('');
  const [matSnap, setMatSnap] = useState(null);
  const [matLoading, setMatLoading] = useState(true);
  const [matError, setMatError] = useState('');
  const [fgSnap, setFgSnap] = useState(null);
  const [fgLoading, setFgLoading] = useState(true);
  const [fgError, setFgError] = useState('');
  const [riskDays, setRiskDays] = useState(3);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifFilterType, setNotifFilterType] = useState('');
  const [notifFilterRead, setNotifFilterRead] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [pressFormsRes, machinesRes, productsRes] = await Promise.all([
          pressFormService.getAll(),
          machineService.getAll(),
          productService.getAll(),
        ]);
        setStats({
          pressForms: pressFormsRes.data.count || pressFormsRes.data.length,
          machines: machinesRes.data.count || machinesRes.data.length,
          products: productsRes.data.count || productsRes.data.length,
          loading: false,
        });
      } catch (error) {
        console.error('Помилка завантаження статистики:', error);
        setStats((s) => ({ ...s, loading: false }));
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadSnap = async () => {
      try {
        setProdLoading(true);
        setProdError('');
        const res = await dashboardService.productionSnapshot({ risk_days: riskDays });
        if (!cancelled) setProdSnap(res.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setProdError('Не вдалося завантажити зріз виробництва');
      } finally {
        if (!cancelled) setProdLoading(false);
      }
    };
    loadSnap();
    return () => {
      cancelled = true;
    };
  }, [riskDays]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setOutLoading(true);
        setOutError('');
        const res = await dashboardService.productionOutputSnapshot({ top_n: 10 });
        if (!cancelled) setOutSnap(res.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setOutError('Не вдалося завантажити дані випуску (звіти виробництва)');
      } finally {
        if (!cancelled) setOutLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setMatLoading(true);
        setMatError('');
        const res = await dashboardService.materialsLogisticsSnapshot({ critical_limit: 40 });
        if (!cancelled) setMatSnap(res.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setMatError('Не вдалося завантажити звіт «Матеріали та логістика»');
      } finally {
        if (!cancelled) setMatLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setFgLoading(true);
        setFgError('');
        const res = await dashboardService.finishedGoodsSnapshot({ top_n: 15 });
        if (!cancelled) setFgSnap(res.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setFgError('Не вдалося завантажити звіт «Готова продукція і відвантаження»');
      } finally {
        if (!cancelled) setFgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchNotif = async () => {
      try {
        setNotifLoading(true);
        const params = { page_size: 100 };
        if (notifFilterType) params.type = notifFilterType;
        if (notifFilterRead === 'read') params.is_read = true;
        if (notifFilterRead === 'unread') params.is_read = false;
        const res = await notificationService.getAll(params);
        setNotifications(res.data.results || res.data);
      } catch (err) {
        console.error('Помилка завантаження подій:', err);
      } finally {
        setNotifLoading(false);
      }
    };
    fetchNotif();

    const refresh = () => {
      if (document.visibilityState === 'visible') {
        fetchNotif();
      }
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', fetchNotif);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', fetchNotif);
    };
  }, [notifFilterType, notifFilterRead]);

  const handleMarkRead = async (ids) => {
    if (!ids.length) return;
    try {
      await notificationService.markRead(ids);
      setNotifications((prev) =>
        prev.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n))
      );
      // Оновити лічильник у дзвіночку без тосту (Layout: лише id > maxSeen)
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (err) {
      console.error('Помилка позначення прочитаним:', err);
    }
  };

  const scrollToDetail = useCallback((elementId) => {
    const el = document.getElementById(elementId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  if (stats.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Головна панель
      </Typography>

      <ExecutiveSummaryCards
        scrollToDetail={scrollToDetail}
        prodSnap={prodSnap}
        prodLoading={prodLoading}
        prodError={prodError}
        outSnap={outSnap}
        outLoading={outLoading}
        outError={outError}
        matSnap={matSnap}
        matLoading={matLoading}
        matError={matError}
        fgSnap={fgSnap}
        fgLoading={fgLoading}
        fgError={fgError}
      />

      <Paper id="dashboard-detail-production" sx={{ p: 2, mb: 3, mt: 0, scrollMarginTop: 16 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Typography variant="h6">Виробництво «зараз» (операційний зріз)</Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{'Ризик: дедлайн < N днів'}</InputLabel>
            <Select
              label="Ризик: дедлайн < N днів"
              value={riskDays}
              onChange={(e) => setRiskDays(Number(e.target.value))}
            >
              {[1, 2, 3, 5, 7, 14].map((d) => (
                <MenuItem key={d} value={d}>
                  {d} дн.
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {prodLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        )}
        {prodError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {prodError}
          </Typography>
        )}
        {!prodLoading && prodSnap && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Оновлено: {fmtDt(prodSnap.generated_at)} · поріг довгої перерви:{' '}
              {prodSnap.params?.long_break_hours ?? 4} год
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              Замовлення за етапами (без скасованих)
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Статус</TableCell>
                    <TableCell align="right">Кількість замовлень</TableCell>
                    <TableCell align="right">Шт (сума)</TableCell>
                    <TableCell align="right">Кг (оцінка*)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(prodSnap.orders_by_status || []).map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">{row.count}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell align="right">
                        {row.kg != null ? row.kg : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              *Кг сировини = шт × вага виробу (у картці вказується в грамах, у розрахунку — кг); якщо вага не задана — 0.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              Просрочені / ризикові (підсумок)
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              <Chip
                size="small"
                label={`Не розпочато після планового старту: ${prodSnap.risky_summary?.late_start ?? 0}`}
                color={prodSnap.risky_summary?.late_start ? 'warning' : 'default'}
              />
              <Chip
                size="small"
                label={`Завершення по плану < ${riskDays} дн.: ${prodSnap.risky_summary?.deadline_soon ?? 0}`}
                color={prodSnap.risky_summary?.deadline_soon ? 'warning' : 'default'}
              />
              <Chip
                size="small"
                label={`Прострочено planned_end (запущено): ${prodSnap.risky_summary?.overdue_plan ?? 0}`}
                color={prodSnap.risky_summary?.overdue_plan ? 'error' : 'default'}
              />
              <Chip
                size="small"
                label={`Дедлайн клієнта < ${riskDays} дн.: ${prodSnap.risky_summary?.customer_deadline_soon ?? 0}`}
                color={prodSnap.risky_summary?.customer_deadline_soon ? 'warning' : 'default'}
              />
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Приклад замовлень з ризиками (до 40)
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Виріб</TableCell>
                    <TableCell>ТПА</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>План заверш.</TableCell>
                    <TableCell>Причини</TableCell>
                    <TableCell align="right">Дії</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(prodSnap.risky_orders_sample || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="textSecondary">Немає записів за поточними критеріями</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    prodSnap.risky_orders_sample.map((r) => (
                      <TableRow key={r.order_id}>
                        <TableCell>{r.order_id}</TableCell>
                        <TableCell>{r.product_name}</TableCell>
                        <TableCell>{r.machine}</TableCell>
                        <TableCell>{r.status}</TableCell>
                        <TableCell>{fmtDt(r.planned_end)}</TableCell>
                        <TableCell>
                          {(r.reasons || []).map((t, i) => (
                            <Typography key={i} variant="body2" display="block">
                              • {t}
                            </Typography>
                          ))}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            component={RouterLink}
                            to={`/production?order=${r.order_id}`}
                            size="small"
                            variant="text"
                          >
                            У планер
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" gutterBottom>
              Завантаження ТПА
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Активних станків
                    </Typography>
                    <Typography variant="h5">{prodSnap.machines?.total_active ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      З замовленням «в роботі»
                    </Typography>
                    <Typography variant="h5">{prodSnap.machines?.with_order_in_progress ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Без активного замовлення
                    </Typography>
                    <Typography variant="h5">{prodSnap.machines?.idle ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Перерви зараз
                    </Typography>
                    <Typography variant="h5">
                      {(prodSnap.machines?.active_breaks || []).length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Typography variant="body2" gutterBottom>
              Активні перерви (простій)
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ТПА</TableCell>
                    <TableCell>З</TableCell>
                    <TableCell>По</TableCell>
                    <TableCell align="right">Годин триває</TableCell>
                    <TableCell>Довга*</TableCell>
                    <TableCell>Причина</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(prodSnap.machines?.active_breaks || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="textSecondary">Немає активних перерв</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    prodSnap.machines.active_breaks.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>{b.machine_identifier}</TableCell>
                        <TableCell>{fmtDt(b.start_at)}</TableCell>
                        <TableCell>{fmtDt(b.end_at)}</TableCell>
                        <TableCell align="right">{b.hours_elapsed}</TableCell>
                        <TableCell>
                          {b.long_break ? (
                            <Chip size="small" label="Так" color="warning" />
                          ) : (
                            'Ні'
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>{b.reason || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              *Довга перерва: триває довше ніж {prodSnap.params?.long_break_hours ?? 4} год від початку.
            </Typography>

            <Typography variant="body2" gutterBottom>
              Найближчі ТО / план обслуговування
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ТПА</TableCell>
                    <TableCell>Початок</TableCell>
                    <TableCell>Кінець</TableCell>
                    <TableCell>Коментар</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(prodSnap.machines?.maintenance_upcoming || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="textSecondary">Немає записів у плані</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    prodSnap.machines.maintenance_upcoming.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{m.machine_identifier}</TableCell>
                        <TableCell>{fmtDt(m.start_at)}</TableCell>
                        <TableCell>{fmtDt(m.end_at)}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>{m.comment || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" gutterBottom>
              Черга сушіння (матеріали з ознакою «потрібне сушіння»)
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Chip
                color="default"
                label={`Очікує: ${prodSnap.drying_queue?.pending ?? 0}`}
              />
              <Chip
                color="primary"
                label={`В процесі: ${prodSnap.drying_queue?.in_progress ?? 0}`}
              />
              <Chip label={`Завершено (усіх): ${prodSnap.drying_queue?.completed ?? 0}`} />
              <Chip label={`Скасовано: ${prodSnap.drying_queue?.cancelled ?? 0}`} />
            </Box>
          </>
        )}
      </Paper>

      <Paper id="dashboard-detail-output" sx={{ p: 2, mb: 3, scrollMarginTop: 16 }}>
        <Typography variant="h6" gutterBottom>
          Випуск і план vs факт
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Виготовлено за період — сума поля «факт» у звітах виробництва (приріст за кожен звіт, шт). Порівняння з
          планом — за тими ж рядками звіту.
        </Typography>
        {outLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={32} />
          </Box>
        )}
        {outError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {outError}
          </Typography>
        )}
        {!outLoading && outSnap && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Оновлено: {fmtDt(outSnap.generated_at)} · {outSnap.timezone}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {['today', 'week', 'month'].map((key) => {
                const p = outSnap.periods?.[key];
                return (
                  <Grid item xs={12} md={4} key={key}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {p?.label}
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 0.5 }}>
                          Факт: {p?.fact ?? '—'} шт
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          План: {p?.plan ?? '—'} шт
                        </Typography>
                        <Chip
                          size="small"
                          sx={{ mt: 1 }}
                          color={deviationChipColor(p?.deviation_pct)}
                          label={`Відхилення: ${fmtDeviationPct(p?.deviation_pct)}`}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Typography variant="subtitle2" gutterBottom>
              Топ відхилень план/факт за місяць ({outSnap.plan_vs_fact?.basis_label}) — замовлення
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Відсоток: (сума факт − сума план) / сума план по рядках звітів за місяць. Показано найбільші за модулем.
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№ замовл.</TableCell>
                    <TableCell>Виріб</TableCell>
                    <TableCell align="right">План, шт</TableCell>
                    <TableCell align="right">Факт, шт</TableCell>
                    <TableCell align="right">Відхилення</TableCell>
                    <TableCell align="right">Дії</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(outSnap.plan_vs_fact?.top_orders || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="textSecondary">Немає рядків звітів з планом за цей місяць</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    outSnap.plan_vs_fact.top_orders.map((r) => (
                      <TableRow key={r.order_id}>
                        <TableCell>{r.order_id}</TableCell>
                        <TableCell>{r.product_name || '—'}</TableCell>
                        <TableCell align="right">{r.sum_plan}</TableCell>
                        <TableCell align="right">{r.sum_fact}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            color={deviationChipColor(r.deviation_pct)}
                            label={fmtDeviationPct(r.deviation_pct)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            component={RouterLink}
                            to={`/production?order=${r.order_id}`}
                            size="small"
                            variant="text"
                          >
                            У планер
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" gutterBottom>
              Топ відхилень — вироби (агреговано по всіх замовленнях за місяць)
            </Typography>
            <TableContainer sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Виріб</TableCell>
                    <TableCell align="right">План, шт</TableCell>
                    <TableCell align="right">Факт, шт</TableCell>
                    <TableCell align="right">Відхилення</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(outSnap.plan_vs_fact?.top_products || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="textSecondary">Немає даних</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    outSnap.plan_vs_fact.top_products.map((r) => (
                      <TableRow key={r.product_id}>
                        <TableCell>{r.product_name || '—'}</TableCell>
                        <TableCell align="right">{r.sum_plan}</TableCell>
                        <TableCell align="right">{r.sum_fact}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            color={deviationChipColor(r.deviation_pct)}
                            label={fmtDeviationPct(r.deviation_pct)}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <Paper id="dashboard-detail-materials" sx={{ p: 2, mb: 3, scrollMarginTop: 16 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
          <Typography variant="h6">Матеріали та логістика (внутрішня)</Typography>
          <Button component={RouterLink} to="/stock" size="small" variant="outlined">
            Надходження (склад)
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Критичні залишки — за мінімумом у довіднику сировини / фарб / упаковки; опційно можна задати глобальний поріг
          через API (<code>fallback_min</code>). Закупівлі та «висячі» прийоми — для контролю логістики.
        </Typography>
        {matLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={32} />
          </Box>
        )}
        {matError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {matError}
          </Typography>
        )}
        {!matLoading && matSnap && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Оновлено: {fmtDt(matSnap.generated_at)}
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              Критичні залишки (нижче мінімуму)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {matSnap.critical_stock?.note}
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Тип</TableCell>
                    <TableCell>Назва</TableCell>
                    <TableCell>Артикул</TableCell>
                    <TableCell align="right">Залишок</TableCell>
                    <TableCell align="right">Мінімум</TableCell>
                    <TableCell align="right">Дефіцит</TableCell>
                    <TableCell>Поріг</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(matSnap.critical_stock?.items || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="textSecondary">
                          Немає позицій нижче порогу (задайте «Мін. залишок» у довідниках або{' '}
                          <code>fallback_min</code> у запиті API)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    matSnap.critical_stock.items.map((r) => (
                      <TableRow key={`${r.kind}-${r.stock_item_id}`}>
                        <TableCell>{stockKindLabel(r.kind)}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.article || '—'}</TableCell>
                        <TableCell align="right">
                          {r.quantity} {r.unit || ''}
                        </TableCell>
                        <TableCell align="right">{r.minimum}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" color="error" label={r.shortage} />
                        </TableCell>
                        <TableCell>
                          {r.threshold_source === 'catalog' ? 'Довідник' : 'Поріг API'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {(matSnap.critical_stock?.count || 0) > (matSnap.critical_stock?.items || []).length && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Показано {matSnap.critical_stock.items.length} з {matSnap.critical_stock.count} критичних позицій.
              </Typography>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Відкриті закупівлі
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      У дорозі (замовлено / у роботі)
                    </Typography>
                    <Typography variant="h6">
                      {matSnap.purchases?.in_transit_value ?? 0} грн
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {matSnap.purchases?.in_transit_count ?? 0} закупівель
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Усі відкриті (не «Виконано»)
                    </Typography>
                    <Typography variant="h6">{matSnap.purchases?.open_total_value ?? 0} грн</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {matSnap.purchases?.open_count ?? 0} закупівель
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Прострочені поставки (план)
                    </Typography>
                    <Typography variant="h6" color={matSnap.purchases?.overdue_count ? 'error' : 'inherit'}>
                      {matSnap.purchases?.overdue_count ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Виконано, очікує прийом на склад
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: matSnap.purchases?.completed_awaiting_receipt_count ? 'warning.main' : 'inherit',
                      }}
                    >
                      {matSnap.purchases?.completed_awaiting_receipt_count ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Box sx={{ mb: 1 }}>
              <Button component={RouterLink} to="/purchases" size="small" variant="text">
                Перейти до закупівель
              </Button>
            </Box>
            <Typography variant="body2" gutterBottom>
              Приклад прострочених (до 20)
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Позиція</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>План прибуття</TableCell>
                    <TableCell>План відгрузки</TableCell>
                    <TableCell align="right">Сума</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(matSnap.purchases?.overdue_sample || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="textSecondary">Немає</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    matSnap.purchases.overdue_sample.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.id}</TableCell>
                        <TableCell>{r.item}</TableCell>
                        <TableCell>{r.status_display || r.status}</TableCell>
                        <TableCell>{fmtDt(r.arrival_date_planned)}</TableCell>
                        <TableCell>{fmtDt(r.delivery_date_planned)}</TableCell>
                        <TableCell align="right">{r.value}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" gutterBottom>
              Виконано — очікується прийом (до 20)
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Позиція</TableCell>
                    <TableCell align="right">Кількість</TableCell>
                    <TableCell>Факт «виконано»</TableCell>
                    <TableCell align="right">Сума</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(matSnap.purchases?.completed_awaiting_receipt_sample || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography color="textSecondary">Немає</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    matSnap.purchases.completed_awaiting_receipt_sample.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.id}</TableCell>
                        <TableCell>{r.item}</TableCell>
                        <TableCell align="right">{r.quantity}</TableCell>
                        <TableCell>{fmtDt(r.arrival_date_actual)}</TableCell>
                        <TableCell align="right">{r.value}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" gutterBottom>
              Надходження без підтвердження
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {matSnap.unconfirmed_receipts?.note}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Рядки «Надходження» (очікує прийом)
                    </Typography>
                    <Typography variant="h5">{matSnap.unconfirmed_receipts?.pending_stock_arrivals ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Готова продукція без підтвердження складу
                    </Typography>
                    <Typography variant="h5">{matSnap.unconfirmed_receipts?.finished_goods_unconfirmed ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Усього «висячих» рядків логістики
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: matSnap.unconfirmed_receipts?.total_logistics_rows ? 'warning.main' : 'inherit',
                      }}
                    >
                      {matSnap.unconfirmed_receipts?.total_logistics_rows ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Надходження: закупівлі {matSnap.unconfirmed_receipts?.breakdown?.from_purchase ?? 0}, упаковка{' '}
              {matSnap.unconfirmed_receipts?.breakdown?.from_packaging ?? 0}, дроблення (Перемол){' '}
              {matSnap.unconfirmed_receipts?.breakdown?.from_crushing ?? 0}
            </Typography>
            <Typography variant="body2" gutterBottom>
              Приклад приходів ГП без підтвердження (до 15)
            </Typography>
            <TableContainer sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Виріб</TableCell>
                    <TableCell align="right">К-сть</TableCell>
                    <TableCell>Склад</TableCell>
                    <TableCell>Створено</TableCell>
                    <TableCell>Замовл.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(matSnap.unconfirmed_receipts?.finished_goods_sample || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="textSecondary">Немає</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    matSnap.unconfirmed_receipts.finished_goods_sample.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.id}</TableCell>
                        <TableCell>{r.product_name}</TableCell>
                        <TableCell align="right">{r.quantity}</TableCell>
                        <TableCell>{r.warehouse || '—'}</TableCell>
                        <TableCell>{fmtDt(r.created_at)}</TableCell>
                        <TableCell>
                          {r.order_id ? (
                            <Button component={RouterLink} to={`/production?order=${r.order_id}`} size="small" variant="text">
                              {r.order_id}
                            </Button>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Button component={RouterLink} to="/stock-finished" size="small" variant="outlined">
              Склад готової продукції
            </Button>
          </>
        )}
      </Paper>

      <Paper id="dashboard-detail-finished-goods" sx={{ p: 2, mb: 3, scrollMarginTop: 16 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
          <Typography variant="h6">Готова продукція і відвантаження</Typography>
          <Button component={RouterLink} to="/stock-finished" size="small" variant="outlined">
            Склад ГП
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Залишки за підтвердженими приходами мінус відвантаження; відвантаження — по даті створення запису (для
          щоденного контролю керівником).
        </Typography>
        {fgLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={32} />
          </Box>
        )}
        {fgError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {fgError}
          </Typography>
        )}
        {!fgLoading && fgSnap && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Оновлено: {fmtDt(fgSnap.generated_at)} · {fgSnap.timezone}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {fgSnap.balance_note}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Виробів з залишком &gt; 0
                    </Typography>
                    <Typography variant="h5">{fgSnap.balances_aggregate?.products_with_stock ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Усього шт на складі ГП
                    </Typography>
                    <Typography variant="h5">{fgSnap.balances_aggregate?.total_units_on_hand ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Відстежуваних виробів (прихід/відвантаження в історії)
                    </Typography>
                    <Typography variant="h5">{fgSnap.balances_aggregate?.products_tracked ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Typography variant="subtitle2" gutterBottom>
              Топ позицій за залишком (шт)
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Виріб</TableCell>
                    <TableCell>Артикул</TableCell>
                    <TableCell align="right">Залишок, шт</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(fgSnap.top_products || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography color="textSecondary">Немає залишків за поточними даними</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    fgSnap.top_products.map((r) => (
                      <TableRow key={r.product_id}>
                        <TableCell>{r.product_name || '—'}</TableCell>
                        <TableCell>{r.product_article || '—'}</TableCell>
                        <TableCell align="right">{r.total_quantity}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" gutterBottom>
              Непідтверджені приходи на склад готової продукції
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
              <Chip
                label={`Рядків: ${fgSnap.unconfirmed_receipts?.count ?? 0}`}
                color={fgSnap.unconfirmed_receipts?.count ? 'warning' : 'default'}
              />
              <Chip
                label={`Шт очікує підтвердження: ${fgSnap.unconfirmed_receipts?.total_quantity_pending ?? 0}`}
                color={fgSnap.unconfirmed_receipts?.total_quantity_pending ? 'warning' : 'default'}
              />
            </Box>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>№</TableCell>
                    <TableCell>Виріб</TableCell>
                    <TableCell align="right">К-сть</TableCell>
                    <TableCell>Склад</TableCell>
                    <TableCell>Створено</TableCell>
                    <TableCell>Замовл.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(fgSnap.unconfirmed_receipts?.sample || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="textSecondary">Усі приходи підтверджені</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    fgSnap.unconfirmed_receipts.sample.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.id}</TableCell>
                        <TableCell>{r.product_name}</TableCell>
                        <TableCell align="right">{r.quantity}</TableCell>
                        <TableCell>{r.warehouse || '—'}</TableCell>
                        <TableCell>{fmtDt(r.created_at)}</TableCell>
                        <TableCell>
                          {r.order_id ? (
                            <Button component={RouterLink} to={`/production?order=${r.order_id}`} size="small" variant="text">
                              {r.order_id}
                            </Button>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" gutterBottom>
              Відвантаження за період
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {fgSnap.shipments?.note}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {['today', 'week', 'month'].map((key) => {
                const p = fgSnap.shipments?.periods?.[key];
                return (
                  <Grid item xs={12} md={4} key={key}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {p?.label}
                        </Typography>
                        <Typography variant="h6">{p?.quantity ?? 0} шт</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {p?.shipments_count ?? 0} операцій відвантаження
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Typography variant="subtitle2" gutterBottom>
              Відвантаження по днях (останні 7 днів)
            </Typography>
            <TableContainer sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell>День</TableCell>
                    <TableCell align="right">Операцій</TableCell>
                    <TableCell align="right">Шт</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(fgSnap.shipments?.daily_last_7_days || []).map((row) => (
                    <TableRow key={row.date}>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{weekdayUk(row.date, row.weekday)}</TableCell>
                      <TableCell align="right">{row.shipments_count}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Прес-форми
              </Typography>
              <Typography variant="h3">{stats.pressForms}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Станки
              </Typography>
              <Typography variant="h3">{stats.machines}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Продукція
              </Typography>
              <Typography variant="h3">{stats.products}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Лог подій
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Тип</InputLabel>
              <Select
                value={notifFilterType}
                label="Тип"
                onChange={(e) => setNotifFilterType(e.target.value)}
              >
                {NOTIFICATION_TYPES.map((t) => (
                  <MenuItem key={t.value || 'all'} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Статус</InputLabel>
              <Select
                value={notifFilterRead}
                label="Статус"
                onChange={(e) => setNotifFilterRead(e.target.value)}
              >
                <MenuItem value="">Усі</MenuItem>
                <MenuItem value="unread">Непрочитані</MenuItem>
                <MenuItem value="read">Прочитані</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Заголовок</TableCell>
                  <TableCell>Повідомлення</TableCell>
                  <TableCell>Прочитано</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">Немає подій</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map((n) => {
                    const dest = getNotificationDestination(n);
                    return (
                    <TableRow key={n.id} sx={{ bgcolor: n.is_read ? undefined : 'action.hover' }}>
                      <TableCell>
                        {n.created_at
                          ? new Date(n.created_at).toLocaleString('uk-UA')
                          : '—'}
                      </TableCell>
                      <TableCell>{n.type_display || n.type}</TableCell>
                      <TableCell>{n.title}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>{n.message}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={n.is_read ? 'Так' : 'Ні'}
                          color={n.is_read ? 'default' : 'primary'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={0.5} justifyContent="flex-end" flexWrap="wrap">
                          {dest ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                navigate({ pathname: dest.pathname, search: dest.search || '' });
                              }}
                            >
                              Перейти
                            </Button>
                          ) : null}
                          {!n.is_read && (
                            <Button size="small" onClick={() => handleMarkRead([n.id])}>
                              Прочитано
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!notifLoading && notifications.some((n) => !n.is_read) && (
            <Button
              sx={{ mt: 1 }}
              size="small"
              onClick={() =>
                handleMarkRead(notifications.filter((n) => !n.is_read).map((n) => n.id))
              }
            >
              Позначити всі прочитаними
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
